import styled from "styled-components";
import { fontFamilies, fontSizes } from "components/shared/tokens/typography";

const MOBILE = "@media (max-width: 480px)";
const DESKTOP = "@media (min-width: 768px)";
const WIDE = "@media (min-width: 900px)";

const COLORS = {
  page: "#f5f7fb",
  card: "#ffffff",
  title: "#2f3545",
  muted: "#7b8193",
  brand: "#ef4f5f",
  cta: "#000000",
  white: "#ffffff",
};

const SHADOW = {
  hero: "0 8px 24px rgba(28, 28, 28, 0.08)",
  heroMobile: "0 6px 18px rgba(28, 28, 28, 0.06)",
  card: "0 8px 22px rgba(34, 45, 65, 0.07)",
};

const RADIUS = {
  card: "20px",
  mobileCard: "16px",
  hero: "18px",
  pill: "999px",
};

const HOME_MAX_WIDTH = "1180px";
const TILE_ICON_SIZE = "64px";

export const Box = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: ${props => props.bg || ""};
`;

export const SubBox = styled(Box)`
  background: ${COLORS.card};
  border-radius: 0 0 ${RADIUS.card} ${RADIUS.card};
  padding: 14px 0;

  ${MOBILE} {
    padding: 12px 0;
    border-radius: 0 0 ${RADIUS.mobileCard} ${RADIUS.mobileCard};
  }
`;

export const HomeSurface = styled.main`
  width: calc(100% + 2em);
  min-height: auto;
  margin: -1em;
  padding: 16px 16px 28px;
  background: ${COLORS.page};

  ${DESKTOP} {
    min-height: calc(100vh - 88px);
    padding: 28px 32px 64px;
  }

  ${MOBILE} {
    padding: 12px 16px 20px;
  }
`;

export const HomeContent = styled.div`
  width: min(100%, ${HOME_MAX_WIDTH});
  margin: 0 auto;
`;

export const HomeSkeleton = styled.div`
  display: grid;
  gap: 16px;
`;

export const SkeletonBlock = styled.div`
  width: 100%;
  height: ${props => props.$height || "120px"};
  border-radius: ${RADIUS.mobileCard};
  background: linear-gradient(90deg, #eef1f6 0%, #f7f9fc 45%, #eef1f6 100%);
  background-size: 220% 100%;
  animation: seller-home-skeleton 1.25s ease-in-out infinite;

  @keyframes seller-home-skeleton {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }
`;

export const HeroCarousel = styled.section`
  position: relative;
`;

export const HeroViewport = styled.div`
  overflow: hidden;
  border-radius: ${RADIUS.hero};
  box-shadow: ${SHADOW.hero};

  ${MOBILE} {
    border-radius: ${RADIUS.mobileCard};
    box-shadow: ${SHADOW.heroMobile};
  }
`;

export const HeroTrack = styled.div`
  display: flex;
  will-change: transform;
`;

export const HeroSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
`;

export const HeroCard = styled.button`
  position: relative;
  width: 100%;
  min-height: 260px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: ${RADIUS.hero};
  padding: 0;
  background:
    linear-gradient(90deg, rgba(38, 18, 11, 0.96) 0%, rgba(38, 18, 11, 0.84) 45%, rgba(38, 18, 11, 0.14) 100%),
    ${props => props.$backgroundImage ? `url("${props.$backgroundImage}") center / cover no-repeat,` : ""}
    ${props => props.$backgroundImage
    ? "linear-gradient(135deg, #7b4d3a 0%, #d4dde3 54%, #eef2f5 100%)"
    : "linear-gradient(135deg, #6b7280 0%, #a8b0bd 55%, #d7dce4 100%)"};
  color: ${COLORS.white};
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &::after {
    content: "";
    position: absolute;
    right: 22px;
    bottom: 22px;
    width: 132px;
    height: 82px;
    background: url("/logos/truck.svg") center / contain no-repeat;
    filter: drop-shadow(0 14px 24px rgba(0, 0, 0, 0.16));
    opacity: 0.92;
    display: ${props => props.$backgroundImage ? "none" : "block"};
  }

  ${DESKTOP} {
    min-height: clamp(248px, 26vw, 360px);
    border-radius: ${RADIUS.card};
  }

  ${MOBILE} {
    min-height: 0;
    height: clamp(136px, 38vw, 148px);
    border-radius: ${RADIUS.mobileCard};

    &::after {
      right: 14px;
      bottom: 14px;
      width: 88px;
      height: 56px;
    }
  }
`;

export const HeroCopy = styled.div`
  position: relative;
  z-index: 1;
  max-width: min(48%, 460px);
  padding: 28px 30px 22px;

  ${MOBILE} {
    max-width: 64%;
    padding: 14px 14px 12px;
  }
`;

export const HeroEyebrow = styled.div`
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.78);
  font-family: ${fontFamilies.semiBold};
  font-size: ${fontSizes.z200};
  font-weight: 600;
  line-height: 1.3;

  ${MOBILE} {
    margin-bottom: 6px;
    font-size: 12px;
    line-height: 1.25;
  }
`;

export const HeroTitle = styled.div`
  margin-bottom: 18px;
  color: ${COLORS.white};
  font-family: ${fontFamilies.bold};
  font-size: ${fontSizes.z500};
  font-weight: 700;
  line-height: 1.3;

  ${DESKTOP} {
    font-size: 34px;
    line-height: 1.18;
  }

  ${MOBILE} {
    margin-bottom: 12px;
    font-size: 20px;
    line-height: 1.22;
  }
`;

export const HeroCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  background: ${COLORS.cta};
  color: ${COLORS.white};
  font-family: ${fontFamilies.bold};
  font-size: ${fontSizes.z300};
  font-weight: 700;

  ${MOBILE} {
    min-height: 36px;
    padding: 0 12px;
    border-radius: 8px;
    font-size: 14px;
  }
`;

export const HeroDots = styled.div`
  position: absolute;
  z-index: 2;
  left: 50%;
  bottom: 12px;
  display: flex;
  gap: 6px;
  transform: translateX(-50%);

  button {
    position: relative;
    width: 8px;
    height: 8px;
    overflow: hidden;
    padding: 0;
    border: 0;
    border-radius: ${RADIUS.pill};
    background: rgba(255, 255, 255, 0.45);
    cursor: pointer;
    transition: width 300ms ease;
  }

  button[data-active="true"] {
    width: 28px;
    background: rgba(255, 255, 255, 0.38);
  }

  button span {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${COLORS.white};
    transform: scaleX(0);
    transform-origin: left center;
    will-change: transform;
  }

  ${MOBILE} {
    bottom: 8px;
    gap: 4px;

    button {
      width: 5px;
      height: 5px;
    }

    button[data-active="true"] {
      width: 24px;
    }
  }
`;

export const ManageOrdersCard = styled.section`
  margin-top: 20px;
  padding: 26px 28px 30px;
  border-radius: ${RADIUS.card};
  background: ${COLORS.card};
  box-shadow: ${SHADOW.card};

  ${WIDE} {
    padding: 30px 36px 34px;
  }

  ${MOBILE} {
    margin-top: 16px;
    padding: 16px 14px 22px;
    border-radius: ${RADIUS.mobileCard};
  }
`;

export const SectionTitle = styled.h1`
  margin: 0 0 22px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: ${fontSizes.z500};
  font-weight: 700;
  line-height: 1.2;

  ${MOBILE} {
    font-size: 20px;
  }
`;

export const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 18px;
  row-gap: 28px;

  ${WIDE} {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    column-gap: 24px;
  }

  ${MOBILE} {
    column-gap: 10px;
    row-gap: 26px;
  }
`;

export const OrderTile = styled.button`
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${COLORS.muted};
  font-family: ${fontFamilies.semiBold};
  font-size: ${fontSizes.z300};
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.72;
  }

  &:disabled img {
    opacity: 0.72;
  }

  ${MOBILE} {
    gap: 9px;
    font-size: 15px;
    line-height: 1.22;
  }
`;

export const TileIcon = styled.span`
  position: relative;
  display: inline-flex;
  width: ${TILE_ICON_SIZE};
  height: ${TILE_ICON_SIZE};
  align-items: center;
  justify-content: center;

  img {
    display: block;
    width: ${TILE_ICON_SIZE};
    height: ${TILE_ICON_SIZE};
  }
`;

export const TileIconFallback = styled.span`
  display: inline-flex;
  width: ${TILE_ICON_SIZE};
  height: ${TILE_ICON_SIZE};
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eef1f6;
  color: #9aa2b1;
`;

export const Badge = styled.span`
  position: absolute;
  top: -2px;
  right: -4px;
  display: inline-flex;
  min-width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border: 2px solid ${COLORS.white};
  border-radius: ${RADIUS.pill};
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.bold};
  font-size: ${fontSizes.z100};
  font-weight: 700;
`;

export const NotificationBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -6px;
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    width: 24px;
    height: 24px;
  }
`;

export const AllPoButton = styled.button`
  background: white;
  width: 100%;
  height: 70px;
  border-radius: 20px;
  border: none;
  box-shadow: 0px 4px 20px rgba(28, 28, 28, 0.04);
  margin-bottom: 10px;
  cursor: pointer;
`;
